import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  Select,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import StatusPill from '../common/StatusPill';

// Styled components - Modern, shadcn-inspired design
const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
});

const BackButton = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.divider,
  },
}));

const WorkflowTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  maxWidth: '300px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

const CompareContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  padding: '16px 20px',
}));

const VersionSelectorsRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '12px',
  flexWrap: 'wrap',
});

const VersionCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const VersionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: theme.palette.text.secondary,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: '200px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: theme.palette.background.paper,
  fontSize: '14px',
  fontWeight: 500,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
    transition: 'border-color 0.15s ease',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.primary,
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    padding: '8px 14px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '14px',
  padding: '10px 16px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.hover,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const SwapIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  color: theme.palette.text.disabled,
  flexShrink: 0,
}));

const Spacer = styled(Box)({
  flex: 1,
});

const RightActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const ViewToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.action.hover,
  borderRadius: '8px',
  padding: '4px',
}));

const ViewButton = styled(IconButton)(({ active, theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  backgroundColor: active ? theme.palette.background.paper : 'transparent',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: active ? theme.palette.background.paper : theme.palette.action.selected,
  },
}));

function StatusChip({ label, status }) {
  const variant =
    status === 'PUBLISHED' || status === 'PRODUCTION' ? 'green' : 'amber';
  return (
    <StatusPill variant={variant} size="sm" uppercase>
      {label}
    </StatusPill>
  );
}

function TopBar({
  goBack,
  workflowName,
  allProjects,
  handleCompareJson,
  selectionOne,
  selectionTwo,
  setSelectionOne,
  setSelectionTwo,
  compareLoad,
  view,
  setView,
}) {
  // Find current draft (highest version) and current published version
  const currentDraftVersion = allProjects?.length > 0 
    ? Math.max(...allProjects.map(p => p.version)) 
    : null;
  
  const currentPublishedVersion = allProjects?.find(
    p => p.projectStatus === 'PRODUCTION' || p.projectStatus === 'PUBLISHED'
  )?.version || null;

  const shouldShowChip = (project) => {
    // Only show chip for current draft or current published
    if (project.version === currentDraftVersion && project.projectStatus === 'DRAFT') return true;
    if (project.version === currentPublishedVersion && 
        (project.projectStatus === 'PRODUCTION' || project.projectStatus === 'PUBLISHED')) return true;
    return false;
  };

  const getStatusLabel = (status) => {
    if (status === 'DRAFT') return 'Draft';
    if (status === 'PRODUCTION' || status === 'PUBLISHED') return 'Published';
    return status;
  };

  return (
    <Box>
      {/* Header with back button and title */}
      <Header>
        <Tooltip title="Back to workflow">
          <BackButton onClick={goBack}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </BackButton>
        </Tooltip>
        <Box>
          <PageTitle>Compare Versions</PageTitle>
          <WorkflowTitle>{workflowName || 'Untitled workflow'}</WorkflowTitle>
        </Box>
      </Header>

      {/* Compare section - single row layout */}
      <CompareContainer>
        <VersionSelectorsRow>
          {/* Left version selector */}
          <VersionCard>
            <VersionLabel>Comparing</VersionLabel>
            <StyledSelect
              value={selectionOne || ''}
              size="small"
              variant="outlined"
              onChange={e => setSelectionOne(e.target.value)}
              disabled={compareLoad}
              displayEmpty
            >
              {allProjects?.map(p => (
                <StyledMenuItem
                  key={`left-${p.version}`}
                  value={`${p.projectStatus}_version_${p.version}`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>Version {p.version}</span>
                    {shouldShowChip(p) && (
                      <StatusChip
                        label={getStatusLabel(p.projectStatus)}
                        status={p.projectStatus}
                        size="small"
                      />
                    )}
                  </Box>
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </VersionCard>

          {/* Swap icon */}
          <SwapIconWrapper>
            <SwapHorizIcon sx={{ fontSize: 18 }} />
          </SwapIconWrapper>

          {/* Right version selector */}
          <VersionCard>
            <VersionLabel>With</VersionLabel>
            <StyledSelect
              value={selectionTwo || ''}
              size="small"
              variant="outlined"
              onChange={e => setSelectionTwo(e.target.value)}
              disabled={compareLoad}
              displayEmpty
            >
              {allProjects?.map(p => (
                <StyledMenuItem
                  key={`right-${p.version}`}
                  value={`${p.projectStatus}_version_${p.version}`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>Version {p.version}</span>
                    {shouldShowChip(p) && (
                      <StatusChip
                        label={getStatusLabel(p.projectStatus)}
                        status={p.projectStatus}
                        size="small"
                      />
                    )}
                  </Box>
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </VersionCard>

          {/* Compare button */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleCompareJson()}
            disabled={compareLoad}
            startIcon={
              compareLoad ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                <CompareArrowsIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{ height: 40, px: '20px', borderRadius: '8px', flexShrink: 0 }}
          >
            {compareLoad ? 'Comparing...' : 'Compare'}
          </Button>

          {/* Spacer to push view toggle right */}
          <Spacer />

          {/* View toggle */}
          <RightActions>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>View:</Typography>
            <ViewToggle>
              <Tooltip title="Split view">
                <ViewButton
                  active={view === 'split_view' ? 1 : 0}
                  onClick={() => setView('split_view')}
                  disabled={compareLoad}
                >
                  <SplitscreenIcon sx={{ fontSize: 18 }} />
                </ViewButton>
              </Tooltip>
              <Tooltip title="Unified view">
                <ViewButton
                  active={view === 'unified_view' ? 1 : 0}
                  onClick={() => setView('unified_view')}
                  disabled={compareLoad}
                >
                  <ViewStreamIcon sx={{ fontSize: 18 }} />
                </ViewButton>
              </Tooltip>
            </ViewToggle>
          </RightActions>
        </VersionSelectorsRow>
      </CompareContainer>
    </Box>
  );
}

export default TopBar;
